<template>
    <div class="analytics-volatility-regime-timeline space-y-4">
        <div v-if="isLoading" class="text-center py-8">
            <div class="inline-flex items-center">
                <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span class="text-sm text-gray-600">Loading volatility regime timeline…</span>
            </div>
        </div>

        <div v-else-if="errorMessage" class="text-center py-8">
            <svg class="mx-auto h-12 w-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <h3 class="mt-2 text-sm font-medium text-gray-900">Unable to load timeline</h3>
            <p class="mt-1 text-sm text-gray-500">{{ errorMessage }}</p>
            <button type="button" class="mt-4 text-sm text-blue-600 hover:text-blue-500" @click="loadTimeline">
                Try Again
            </button>
        </div>

        <div v-else-if="timeline.length === 0" class="rounded-md border border-dashed border-gray-300 bg-gray-50 px-4 py-5 text-sm text-gray-600">
            No volatility regime timeline data is available for this meter yet.
        </div>

        <template v-else>
            <div class="rounded-lg border border-gray-200 bg-slate-50 px-4 py-3">
                <div class="text-xs uppercase tracking-wide text-gray-500">Current Regime</div>
                <div class="mt-1 flex flex-wrap items-center gap-3">
                    <span class="inline-flex items-center rounded-full px-3 py-1 text-sm font-semibold" :class="regimeBadgeClass(currentRegime)">
                        {{ currentRegimeLabel }}
                    </span>
                    <span class="text-sm text-gray-700">
                        Duration: <strong>{{ currentRegimeDurationLabel }}</strong>
                    </span>
                    <span v-if="runConfidencePercent !== null && !insightsSimplified" class="text-xs text-gray-500">
                        Run confidence: {{ runConfidencePercent }}%
                    </span>
                </div>
            </div>

            <div class="space-y-2">
                <div class="relative">
                    <div class="h-8 flex overflow-hidden rounded-md border border-gray-300">
                        <div
                            v-for="(segment, idx) in timeline"
                            :key="idx"
                            class="h-full flex items-center justify-center text-[11px] font-medium text-white/95 px-1"
                            :style="{ width: segmentWidth(segment) + '%', backgroundColor: regimeColor(segment.regime) }"
                            :title="segmentTooltip(segment)"
                        >
                            <span v-if="segmentWidth(segment) >= 9">{{ shortRegimeLabel(segment.regime_label) }}</span>
                        </div>
                    </div>
                    <button
                        v-for="(transition, idx) in transitions"
                        :key="'transition-' + idx"
                        type="button"
                        class="absolute top-[-3px] h-10 w-2 rounded-full border border-white shadow transition hover:scale-110"
                        :style="{ left: markerPosition(transition) + '%', backgroundColor: sourceColor(transition.source) }"
                        :title="transitionTooltip(transition)"
                    ></button>
                </div>
                <div class="flex items-center justify-between text-xs text-gray-500">
                    <span>{{ rangeStartLabel }}</span>
                    <span>{{ rangeEndLabel }}</span>
                </div>
            </div>

            <div class="flex flex-wrap gap-4 text-xs text-gray-600">
                <span class="inline-flex items-center gap-1.5"><span class="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500"></span>Low</span>
                <span class="inline-flex items-center gap-1.5"><span class="inline-block w-2.5 h-2.5 rounded-full bg-sky-500"></span>Moderate</span>
                <span class="inline-flex items-center gap-1.5"><span class="inline-block w-2.5 h-2.5 rounded-full bg-amber-500"></span>High</span>
                <span class="inline-flex items-center gap-1.5"><span class="inline-block w-2.5 h-2.5 rounded-full bg-rose-600"></span>Very high</span>
                <span class="inline-flex items-center gap-1.5"><span class="inline-block w-2.5 h-2.5 rounded-full bg-gray-900"></span>Transition marker</span>
            </div>

            <div v-if="transitions.length > 0 && !insightsSimplified" class="rounded-lg border border-gray-200 bg-white overflow-hidden">
                <div class="px-4 py-2 border-b border-gray-200 text-sm font-semibold text-gray-900">
                    Regime transitions
                </div>
                <div class="overflow-x-auto">
                    <table class="min-w-full text-sm">
                        <thead class="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                            <tr>
                                <th class="px-4 py-2 text-left">Timestamp</th>
                                <th class="px-4 py-2 text-left">From</th>
                                <th class="px-4 py-2 text-left">To</th>
                                <th class="px-4 py-2 text-left">Confidence</th>
                                <th class="px-4 py-2 text-left">Source</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(transition, idx) in transitions" :key="'row-' + idx" class="border-t border-gray-100">
                                <td class="px-4 py-2 text-gray-700">{{ formatDateTime(transition.timestamp) }}</td>
                                <td class="px-4 py-2 text-gray-700">{{ prettyRegime(transition.from_regime) }}</td>
                                <td class="px-4 py-2 text-gray-700">{{ prettyRegime(transition.to_regime) }}</td>
                                <td class="px-4 py-2 text-gray-700">{{ confidenceLabel(transition.confidence) }}</td>
                                <td class="px-4 py-2 text-gray-700">{{ transition.source_label || prettySource(transition.source) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import axios from 'axios';
import { SonicuCoreMixin } from '@/mixins/SonicuCoreMixin';

export default {
    name: 'AnalyticsVolatilityRegimeTimeline',
    mixins: [SonicuCoreMixin],
    props: {
        meterId: {
            type: Number,
            required: true,
        },
        range: {
            type: String,
            default: '90d',
        },
    },
    data() {
        return {
            isLoading: true,
            errorMessage: '',
            timeline: [],
            transitions: [],
            currentRegime: null,
            currentRegimeLabel: 'Unknown',
            regimeDurationSeconds: null,
            runConfidence: null,
            insightsSimplified: false,
        };
    },
    mounted() {
        this.bindInsightsMode();
        this.loadTimeline();
    },
    beforeUnmount() {
        if (this._insightsModeHandler) {
            window.removeEventListener('advanced-analytics-insights-mode', this._insightsModeHandler);
        }
    },
    methods: {
        bindInsightsMode() {
            const apply = () => {
                this.insightsSimplified =
                    typeof window !== 'undefined' && window.__ADVANCED_ANALYTICS_INSIGHTS_MODE === 'simple';
            };
            apply();
            this._insightsModeHandler = () => apply();
            window.addEventListener('advanced-analytics-insights-mode', this._insightsModeHandler);
        },
        async loadTimeline() {
            this.isLoading = true;
            this.errorMessage = '';

            try {
                const response = await axios.get(
                    this.company.buildCompanyApiInternalRoute(`analytics/meters/${this.meterId}/volatility-regime-timeline`),
                    {
                        params: {
                            range: this.range,
                        },
                    },
                );

                if (!response.data.success) {
                    this.timeline = [];
                    this.transitions = [];
                    this.currentRegime = null;
                    this.currentRegimeLabel = 'Unknown';
                    this.regimeDurationSeconds = null;
                    this.runConfidence = null;
                    this.isLoading = false;
                    return;
                }

                this.timeline = Array.isArray(response.data.timeline) ? response.data.timeline : [];
                this.transitions = Array.isArray(response.data.transitions) ? response.data.transitions : [];
                this.currentRegime = response.data.current_regime || null;
                this.currentRegimeLabel = response.data.current_regime_label || this.prettyRegime(this.currentRegime);
                this.regimeDurationSeconds = Number.isFinite(Number(response.data?.regime_duration?.seconds))
                    ? Number(response.data.regime_duration.seconds)
                    : null;
                this.runConfidence = Number.isFinite(Number(response.data.run_confidence))
                    ? Number(response.data.run_confidence)
                    : null;

                this.isLoading = false;
            } catch (error) {
                this.errorMessage = error.response?.data?.message || error.message || 'Failed to load volatility regime timeline.';
                this.isLoading = false;
            }
        },
        regimeColor(regime) {
            const map = {
                low: '#10b981',
                moderate: '#0ea5e9',
                high: '#f59e0b',
                very_high: '#e11d48',
            };
            return map[regime] || '#6b7280';
        },
        sourceColor(source) {
            const map = {
                both: '#d97706',
                ml_only: '#2563eb',
                statistical_only: '#111827',
            };
            return map[source] || '#374151';
        },
        regimeBadgeClass(regime) {
            const map = {
                low: 'bg-emerald-100 text-emerald-800',
                moderate: 'bg-sky-100 text-sky-800',
                high: 'bg-amber-100 text-amber-900',
                very_high: 'bg-rose-100 text-rose-900',
            };
            return map[regime] || 'bg-gray-100 text-gray-800';
        },
        segmentWidth(segment) {
            const total = Math.max(1, this.timelineSpanMs);
            const duration = Math.max(0, Number(segment.end_ms) - Number(segment.start_ms));
            return Math.max(2, (duration / total) * 100);
        },
        markerPosition(transition) {
            if (this.timelineSpanMs <= 0) {
                return 0;
            }
            const raw = ((Number(transition.timestamp_ms) - this.timelineStartMs) / this.timelineSpanMs) * 100;
            return Math.min(100, Math.max(0, raw));
        },
        segmentTooltip(segment) {
            return `${segment.regime_label}\n${this.formatDateTime(segment.start_timestamp)} → ${this.formatDateTime(segment.end_timestamp)}\nAvg volatility: ${this.numberLabel(segment.average_volatility)}`;
        },
        transitionTooltip(transition) {
            return `${this.formatDateTime(transition.timestamp)}\n${this.prettyRegime(transition.from_regime)} → ${this.prettyRegime(transition.to_regime)}\nConfidence: ${this.confidenceLabel(transition.confidence)}\nSource: ${transition.source_label || this.prettySource(transition.source)}`;
        },
        prettyRegime(value) {
            if (!value || typeof value !== 'string') {
                return 'Unknown';
            }
            const normalized = value.replace(/_/g, ' ');
            return normalized.charAt(0).toUpperCase() + normalized.slice(1);
        },
        shortRegimeLabel(label) {
            if (!label) {
                return '';
            }
            return label.replace(' volatility', '');
        },
        prettySource(source) {
            if (!source || typeof source !== 'string') {
                return 'Unknown';
            }
            if (source === 'ml_only') {
                return 'ML';
            }
            if (source === 'statistical_only') {
                return 'Statistical';
            }
            if (source === 'both') {
                return 'ML + statistical';
            }
            return source.replace(/_/g, ' ');
        },
        confidenceLabel(confidence) {
            if (!Number.isFinite(Number(confidence))) {
                return 'N/A';
            }
            return `${(Number(confidence) * 100).toFixed(0)}%`;
        },
        numberLabel(value) {
            if (!Number.isFinite(Number(value))) {
                return 'N/A';
            }
            return Number(value).toFixed(4);
        },
        formatDateTime(value) {
            if (!value) {
                return 'Unknown';
            }
            const d = new Date(value);
            if (Number.isNaN(d.getTime())) {
                return String(value);
            }
            return d.toLocaleString();
        },
        formatDuration(seconds) {
            if (!Number.isFinite(Number(seconds)) || Number(seconds) <= 0) {
                return 'N/A';
            }
            const total = Math.floor(Number(seconds));
            const days = Math.floor(total / 86400);
            const hours = Math.floor((total % 86400) / 3600);
            const mins = Math.floor((total % 3600) / 60);
            if (days > 0) {
                return `${days}d ${hours}h`;
            }
            if (hours > 0) {
                return `${hours}h ${mins}m`;
            }
            return `${mins}m`;
        },
    },
    computed: {
        timelineStartMs() {
            if (!this.timeline.length) {
                return 0;
            }
            return Number(this.timeline[0].start_ms) || 0;
        },
        timelineEndMs() {
            if (!this.timeline.length) {
                return 0;
            }
            return Number(this.timeline[this.timeline.length - 1].end_ms) || 0;
        },
        timelineSpanMs() {
            return Math.max(0, this.timelineEndMs - this.timelineStartMs);
        },
        currentRegimeDurationLabel() {
            return this.formatDuration(this.regimeDurationSeconds);
        },
        runConfidencePercent() {
            if (!Number.isFinite(Number(this.runConfidence))) {
                return null;
            }
            return (Number(this.runConfidence) * 100).toFixed(0);
        },
        rangeStartLabel() {
            return this.timeline.length ? this.formatDateTime(this.timeline[0].start_timestamp) : '';
        },
        rangeEndLabel() {
            return this.timeline.length ? this.formatDateTime(this.timeline[this.timeline.length - 1].end_timestamp) : '';
        },
    },
};
</script>

<style scoped>
.analytics-volatility-regime-timeline {
    width: 100%;
}
</style>
